import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { leaveService } from '../services/leaveService';
import { employeeService } from '../services/api';
import LeaveList from './LeaveList';
import LeaveCalendar from './LeaveCalendar';
import './LeaveManagement.css';

const LeaveManagement = () => {
  const { user, hasRole } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  
  // États pour la génération du rapport de congés
  const [reportType, setReportType] = useState('annual'); // 'annual' ou 'period'
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportFormat, setReportFormat] = useState('pdf'); // 'pdf' ou 'excel'
  const [generatingReport, setGeneratingReport] = useState(false);

  const { hasModulePermission } = useAuth();
  const canApprove = hasModulePermission('LEAVES', 'approve');
  const canDelete = hasModulePermission('LEAVES', 'delete');
  const canViewAll = hasModulePermission('LEAVES', 'view');

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let response;
      if (selectedEmployeeId) {
        response = await leaveService.getByEmployeeId(selectedEmployeeId);
      } else if (filterStatus !== 'ALL') {
        response = await leaveService.getByStatus(filterStatus);
      } else {
        response = await leaveService.getAll();
      }
      let data = response.data || [];
      if (filterStatus !== 'ALL') {
        data = data.filter((l) => l.status === filterStatus || (filterStatus === 'MANAGER_APPROVED' && l.status === 'MANAGER_APPROVED'));
      }
      // Trier par date de début décroissante (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
        const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
        return dateB - dateA; // Décroissant
      });
      setLeaves(data);
      setPage(1);
    } catch (err) {
      setError('Erreur lors du chargement des congés');
      console.error('Error loading leaves:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, selectedEmployeeId]);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await employeeService.getAll();
      setEmployees(res.data || []);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadLeaves();
    loadEmployees();
  }, [loadLeaves, loadEmployees]);

  const totalPages = Math.max(1, Math.ceil(leaves.length / pageSize));
  const displayedLeaves = leaves.slice((page - 1) * pageSize, page * pageSize);
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleApprove = async (leaveId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir approuver ce congé ?')) {
      return;
    }

    try {
      await leaveService.approve(leaveId);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'approbation';
      setError(errorMessage);
    }
  };

  const handleReject = async (leaveId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason === null) return; // User cancelled

    try {
      await leaveService.reject(leaveId, reason);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors du rejet';
      setError(errorMessage);
    }
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce congé ?')) {
      return;
    }

    try {
      await leaveService.delete(leaveId);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la suppression';
      setError(errorMessage);
    }
  };

  const handleApproveManager = async (leaveId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir approuver ce congé en tant que manager ?')) {
      return;
    }

    try {
      await leaveService.approveByManager(leaveId, user?.username);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'approbation par le manager';
      setError(errorMessage);
    }
  };

  const handleRejectManager = async (leaveId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason === null) return; // User cancelled

    try {
      await leaveService.rejectByManager(leaveId, reason);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors du rejet par le manager';
      setError(errorMessage);
    }
  };

  const handleApproveRH = async (leaveId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir approuver ce congé en tant que RH ?')) {
      return;
    }

    try {
      await leaveService.approveByRH(leaveId, user?.username);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'approbation par la RH';
      setError(errorMessage);
    }
  };

  const handleRejectRH = async (leaveId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason === null) return; // User cancelled

    try {
      await leaveService.rejectByRH(leaveId, reason);
      setError('');
      loadLeaves();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors du rejet par la RH';
      setError(errorMessage);
    }
  };
  
  const filteredEmployees = employees.filter((emp) => {
    if (!employeeSearchQuery) return true;
    const q = employeeSearchQuery.toLowerCase();
    const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
    const matricule = (emp.matricule || '').toLowerCase();
    const cin = (emp.cin || '').toLowerCase();
    const cnss = (emp.cnssNumber || '').toLowerCase();
    return fullName.includes(q) || matricule.includes(q) || cin.includes(q) || cnss.includes(q);
  });

  const handleEmployeeSelect = (employee) => {
    if (employee) {
      setSelectedEmployeeId(employee.id);
      setEmployeeSearchQuery(`${employee.nom} ${employee.prenom}`);
    } else {
      setSelectedEmployeeId(null);
      setEmployeeSearchQuery('');
    }
    setShowEmployeeDropdown(false);
  };

  const clearFilters = () => {
    setSelectedEmployeeId(null);
    setEmployeeSearchQuery('');
    setFilterStatus('ALL');
  };

  const handleGenerateReport = async () => {
    if (reportType === 'annual' && !reportYear) {
      setError('Veuillez sélectionner une année');
      return;
    }
    if (reportType === 'period' && (!reportStartDate || !reportEndDate)) {
      setError('Veuillez sélectionner une période (début et fin)');
      return;
    }
    if (reportType === 'period' && new Date(reportStartDate) > new Date(reportEndDate)) {
      setError('La date de début doit être antérieure à la date de fin');
      return;
    }
    
    setGeneratingReport(true);
    setError('');
    try {
      let response;
      let fileName;
      
      if (reportType === 'annual') {
        response = await leaveService.generateAnnualReport(reportYear, reportFormat);
        fileName = `etat_conge_${reportYear}.${reportFormat === 'excel' ? 'xlsx' : 'pdf'}`;
      } else {
        response = await leaveService.generatePeriodReport(reportStartDate, reportEndDate, reportFormat);
        fileName = `etat_conge_${reportStartDate}_${reportEndDate}.${reportFormat === 'excel' ? 'xlsx' : 'pdf'}`;
      }
      
      // Extraire le nom du fichier depuis les headers Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Créer un blob et télécharger
      const blob = new Blob([response.data], { 
        type: reportFormat === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors de la génération du rapport: ' + (err.response?.data?.error || err.message));
      console.error('Error generating leave report:', err);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <div className="leave-management">
      <div className="leave-header">
        <h2>Gestion des Congés et Absences</h2>
        <div className="header-actions">
          <span className="info-text">
            Pour créer une demande, utilisez le bouton 🏖️ dans la liste des employés
          </span>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="view-controls">
        <div className="view-toggle">
          <button
            onClick={() => setViewMode('list')}
            className={viewMode === 'list' ? 'btn btn-active' : 'btn btn-secondary'}
          >
            📋 Liste
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={viewMode === 'calendar' ? 'btn btn-active' : 'btn btn-secondary'}
          >
            📅 Calendrier
          </button>
        </div>
        {(canViewAll || viewMode === 'calendar') && (
          <div className="filters-section">
            <div className="filters-row">
              <div className="filter-group employee-filter">
                <label>Employé</label>
                <div className="employee-select-wrapper">
                  <input
                    type="text"
                    className="employee-search-input"
                    placeholder="Rechercher un employé..."
                    value={employeeSearchQuery}
                    onChange={(e) => {
                      setEmployeeSearchQuery(e.target.value);
                      setShowEmployeeDropdown(true);
                      if (!e.target.value) {
                        setSelectedEmployeeId(null);
                      }
                    }}
                    onFocus={() => setShowEmployeeDropdown(true)}
                    onBlur={() => setTimeout(() => setShowEmployeeDropdown(false), 150)}
                  />
                  {selectedEmployeeId && (
                    <button
                      className="clear-employee-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEmployeeSelect(null);
                      }}
                      title="Effacer"
                    >
                      ×
                    </button>
                  )}
                  {showEmployeeDropdown && filteredEmployees.length > 0 && (
                    <div
                      className="employee-dropdown"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {filteredEmployees.slice(0, 10).map((emp) => (
                        <div
                          key={emp.id}
                          className={`dropdown-item ${selectedEmployeeId === emp.id ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEmployeeSelect(emp);
                          }}
                        >
                          <div className="employee-option">
                            <span className="employee-name">{emp.nom} {emp.prenom}</span>
                            {emp.matricule && <span className="employee-matricule">{emp.matricule}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {canViewAll && (
                <div className="filter-group">
                  <label>Statut</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                  >
                    <option value="ALL">Tous</option>
                    <option value="PENDING">En attente</option>
                    <option value="MANAGER_APPROVED">Approuvé par manager</option>
                    <option value="APPROVED">Approuvés</option>
                    <option value="REJECTED">Rejetés</option>
                  </select>
                </div>
              )}

              {(selectedEmployeeId || filterStatus !== 'ALL') && (
                <div className="filter-group">
                  <button className="btn btn-secondary clear-filters-btn" onClick={clearFilters}>
                    Effacer les filtres
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Section génération du rapport de congés */}
      {canViewAll && (
        <div className="report-generation-section" style={{ 
          marginTop: '1rem', 
          padding: '1rem', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>📊 Rapport de Congés</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Type :</label>
              <select
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  if (e.target.value === 'annual') {
                    setReportStartDate('');
                    setReportEndDate('');
                  }
                }}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="annual">Année</option>
                <option value="period">Période</option>
              </select>
            </div>
            
            {reportType === 'annual' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Année :</label>
                <input
                  type="number"
                  value={reportYear}
                  onChange={(e) => setReportYear(parseInt(e.target.value))}
                  min="2020"
                  max="2100"
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', width: '100px' }}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Date début :</label>
                  <input
                    type="date"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Date fin :</label>
                  <input
                    type="date"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
              </>
            )}
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Format :</label>
              <select
                value={reportFormat}
                onChange={(e) => setReportFormat(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
              </select>
            </div>
            
            <button
              className="btn btn-primary"
              onClick={handleGenerateReport}
              disabled={generatingReport || (reportType === 'annual' && !reportYear) || 
                       (reportType === 'period' && (!reportStartDate || !reportEndDate))}
              style={{ padding: '0.5rem 1rem', height: 'fit-content' }}
            >
              {generatingReport ? 'Génération...' : '📄 Générer le rapport'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : viewMode === 'calendar' ? (
        <LeaveCalendar employeeId={selectedEmployeeId} />
      ) : (
        <>
          <LeaveList
            leaves={displayedLeaves}
            onApprove={handleApprove}
            onReject={handleReject}
            onApproveManager={handleApproveManager}
            onRejectManager={handleRejectManager}
            onApproveRH={handleApproveRH}
            onRejectRH={handleRejectRH}
            onDelete={handleDelete}
            onRefresh={loadLeaves}
          />
          {leaves.length > 0 && (
            <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="btn btn-secondary" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
                Précédent
              </button>
              <span>Page {page} / {totalPages}</span>
              <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {showRequestForm && (
        <div className="modal-overlay" onClick={() => {
          setShowRequestForm(false);
        }}>
          <div className="modal-content leave-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nouvelle demande de congé</h3>
              <button className="close-button" onClick={() => {
                setShowRequestForm(false);
              }}>×</button>
            </div>
            <div className="modal-body">
              <p>Pour créer une demande de congé, utilisez le bouton 🏖️ dans la liste des employés.</p>
              <button onClick={() => {
                setShowRequestForm(false);
              }} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;

